/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import {
  Alert,
  Divider,
  Drawer,
  List,
  Progress,
  Space,
  Tag,
  Typography,
} from "antd";
import { WarningOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { getResumeExperienceLevel } from "./resumeValidation";
import {
  buildResumeQualitySnapshot,
  getTextSuggestions,
} from "./resumeQuality";

const { Text } = Typography;

export default function ATSChecker({
  resumeData,
  jobDescription = "",
  showATS,
  setShowATS,
}) {
  const [issues, setIssues] = useState([]);
  const [score, setScore] = useState(100);
  const [keywordDensity, setKeywordDensity] = useState([]);
  const [skillGap, setSkillGap] = useState([]);
  const [resumeType, setResumeType] = useState("fresher");
  const [platformScores, setPlatformScores] = useState({
    generic: 100,
    naukri: 100,
    linkedin: 100,
  });
  const [qualitySnapshot, setQualitySnapshot] = useState({
    correctedResume: null,
    appliedCorrections: [],
    companyIssues: [],
    projectIssues: [],
  });
  const drawerBackground =
    "linear-gradient(180deg, rgba(15, 23, 42, 0.96), rgba(2, 6, 23, 0.98))";
  const glassCardBackground =
    "linear-gradient(180deg, rgba(15, 23, 42, 0.78), rgba(15, 23, 42, 0.64))";
  const glassCardBorder = "1px solid rgba(148, 163, 184, 0.16)";
  const subtleText = "#94a3b8";
  const bodyText = "#e2e8f0";
  const titleText = "#f8fafc";
  const ATS_STOPWORDS = new Set([
    "a",
    "an",
    "and",
    "are",
    "as",
    "at",
    "be",
    "by",
    "for",
    "from",
    "have",
    "in",
    "into",
    "is",
    "of",
    "on",
    "or",
    "the",
    "to",
    "with",
    "using",
    "used",
    "user",
    "users",
    "work",
    "worked",
    "team",
    "teams",
    "role",
    "resume",
    "profile",
    "project",
    "projects",
    "experience",
  ]);

  const hasText = (v) => typeof v === "string" && v.trim().length > 0;

  const getSelectedResumeType = () => {
    const experienceLevel = String(
      sessionStorage.getItem("experienceLevel") || ""
    )
      .trim()
      .toLowerCase();

    if (experienceLevel === "fresher") return "fresher";
    if (
      experienceLevel === "experience" ||
      experienceLevel === "experienced"
    ) {
      return "experienced";
    }

    return null;
  };

  const getNumericExperienceValue = (value) => {
    const normalizedValue = String(value ?? "").trim();

    if (!normalizedValue || !/^(?:0|[1-9]\d*)(?:\.\d+)?$/.test(normalizedValue)) {
      return null;
    }

    const parsedValue = Number.parseFloat(normalizedValue);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  };

  const stripHTML = (html) => {
    if (!html) return "";
    return String(html).replace(/<[^>]*>/g, "");
  };

  const hasStartedExperienceEntry = (experience) => {
    const descriptionText = stripHTML(experience?.description || "");

    return [
      experience?.company,
      experience?.role,
      experience?.project,
      experience?.startDate,
      experience?.endDate,
      experience?.years,
      descriptionText,
    ].some(hasText);
  };
  const computeKeywordDensity = (text = "") => {
    const words = text
      .toLowerCase()
      .replace(/[^\w\s]/g, " ")
      .split(/\s+/)
      .filter(Boolean);

    const map = {};

    words.forEach((word) => {
      if (word.length > 4) {
        map[word] = (map[word] || 0) + 1;
      }
    });

    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);
  };

const analyzeBullets = (experiences, problems) => {
  const techKeywords = [
    "react",
    "node",
    "nodejs",
    "python",
    "java",
    "aws",
    "docker",
    "kubernetes",
    "sql",
    "mongodb",
    "typescript",
    "nextjs",
    "angular",
    "vue",
    "c#",
    ".net",
  ];

  experiences.forEach((exp, index) => {
    const desc = stripHTML(exp?.description || "");
    // Normalize text for technology detection
    const normalized = desc
      .toLowerCase()
      .replace(/[^\w\s]/g, " ");

    const hasTechnology = techKeywords.some((tech) =>
      normalized.includes(tech)
    );
    // Check missing technologies once per experience
    if (!hasTechnology) {
      problems.push({
        problem: `Experience ${index + 1} missing technologies`,
        suggestion:
          "Mention technologies used (e.g., React, Node.js, Python, AWS).",
      });
    }
  });
};

  const clampScore = (value) => Math.max(0, Math.min(100, Math.round(value)));

  const extractKeywords = (text = "") => {
    const counts = {};

    String(text)
      .toLowerCase()
      .replace(/[^a-z0-9+#.\s]/g, " ")
      .split(/\s+/)
      .filter(Boolean)
      .forEach((word) => {
        if (word.length < 3) return;
        if (ATS_STOPWORDS.has(word)) return;
        counts[word] = (counts[word] || 0) + 1;
      });

    return Object.entries(counts)
      .sort((left, right) => right[1] - left[1])
      .map(([word]) => word);
  };

  const getResumeKeywordSet = (text = "", skills = []) => {
    const keywords = extractKeywords(text).slice(0, 60);
    const normalizedSkills = Array.isArray(skills)
      ? skills
          .map((skill) =>
            String(skill || "")
              .toLowerCase()
              .replace(/[^a-z0-9+#.\s]/g, " ")
              .trim(),
          )
          .filter(Boolean)
      : [];

    return new Set(
      [...keywords, ...normalizedSkills].flatMap((entry) =>
        entry.split(/\s+/).filter(Boolean),
      ),
    );
  };

  const getJobKeywordMatch = (jobText = "", resumeText = "", skills = []) => {
    if (!hasText(jobText)) {
      return {
        jdKeywords: [],
        matchedKeywords: [],
        missingKeywords: [],
        matchRatio: 1,
      };
    }

    const jdKeywords = extractKeywords(jobText).slice(0, 15);
    const resumeKeywords = getResumeKeywordSet(resumeText, skills);
    const matchedKeywords = jdKeywords.filter((keyword) =>
      resumeKeywords.has(keyword),
    );
    const missingKeywords = jdKeywords.filter(
      (keyword) => !resumeKeywords.has(keyword),
    );

    return {
      jdKeywords,
      matchedKeywords,
      missingKeywords,
      matchRatio: jdKeywords.length
        ? matchedKeywords.length / jdKeywords.length
        : 1,
    };
  };

  const getMetricRichExperienceCount = (experiences = []) =>
    experiences.filter((experience) => {
      const description = stripHTML(experience?.description || "");
      return /\b\d+(?:\.\d+)?%?\b/.test(description);
    }).length;

  const buildPlatformScores = ({
    isExperiencedResume,
    analyzedResume,
    experiences,
    education,
    skills,
    summaryText,
    objectiveText,
    achievementsText,
    totalExperienceValue,
    relevantExperienceValue,
    resumeText,
    keywordMatch,
  }) => {
    const profileProblems = [];
    let naukriScore = 100;
    let linkedinScore = 100;
    const metricRichExperienceCount = getMetricRichExperienceCount(experiences);

    const pushPlatformIssue = (platform, problem, suggestion, penalty) => {
      profileProblems.push({
        problem: `${platform}: ${problem}`,
        suggestion,
      });

      if (platform === "Naukri") {
        naukriScore -= penalty;
      } else {
        linkedinScore -= penalty;
      }
    };

    if (!hasText(analyzedResume?.roleTitle) && !hasText(analyzedResume?.role)) {
      pushPlatformIssue(
        "Naukri",
        "Profile headline missing",
        "Add a clear role title or target headline like Frontend Developer, Java Developer, or Data Analyst.",
        10,
      );
      pushPlatformIssue(
        "LinkedIn",
        "Headline missing",
        "Add a strong professional headline that matches the role you want recruiters to find.",
        10,
      );
    }

    if (!hasText(analyzedResume?.location)) {
      pushPlatformIssue(
        "Naukri",
        "Location missing",
        "Add your city so Naukri filters and recruiter searches can place your resume correctly.",
        6,
      );
      pushPlatformIssue(
        "LinkedIn",
        "Location missing",
        "Add your location to improve profile completeness and recruiter discoverability.",
        4,
      );
    }

    if (!hasText(analyzedResume?.phone) || !hasText(analyzedResume?.email)) {
      pushPlatformIssue(
        "Naukri",
        "Contact details incomplete",
        "Keep both phone number and email visible for recruiter callbacks and profile completeness.",
        8,
      );
      pushPlatformIssue(
        "LinkedIn",
        "Contact details incomplete",
        "Keep your resume contact section complete with phone and email.",
        5,
      );
    }

    if (isExperiencedResume) {
      if (!(Number.isFinite(totalExperienceValue) && totalExperienceValue > 0)) {
        pushPlatformIssue(
          "Naukri",
          "Total experience missing",
          "Add total years of experience exactly as recruiters expect in Naukri-style profiles.",
          10,
        );
      }

      if (!(Number.isFinite(relevantExperienceValue) && relevantExperienceValue > 0)) {
        pushPlatformIssue(
          "Naukri",
          "Relevant experience missing",
          "Add relevant experience for the target role to improve ranking in recruiter searches.",
          8,
        );
      }
    }

    if (!isExperiencedResume && experiences.length === 0 && !hasText(achievementsText)) {
      pushPlatformIssue(
        "Naukri",
        "No project proof for fresher profile",
        "Add internships, academic projects, certifications, or achievements so the profile is not seen as empty.",
        10,
      );
    }

    if (!hasText(summaryText) && !hasText(objectiveText)) {
      pushPlatformIssue(
        "LinkedIn",
        "About section missing",
        "Add a short summary that explains your strengths, target role, and impact.",
        10,
      );
    }

    if (skills.length < (isExperiencedResume ? 5 : 3)) {
      pushPlatformIssue(
        "Naukri",
        "Too few listed skills",
        "Add more role-specific skills and tools so recruiter keyword matches improve.",
        8,
      );
      pushPlatformIssue(
        "LinkedIn",
        "Too few listed skills",
        "Add more relevant skills so the profile reads as complete and searchable.",
        6,
      );
    }

    if (experiences.length > 0 && metricRichExperienceCount === 0) {
      pushPlatformIssue(
        "LinkedIn",
        "Experience lacks measurable impact",
        "Add metrics like percentages, counts, revenue, speed, or scale to make the profile stronger.",
        8,
      );
    }

    if (experiences.length === 0) {
      pushPlatformIssue(
        "LinkedIn",
        "Experience section too thin",
        "Add internships, projects, freelance work, or volunteering so the profile looks active.",
        8,
      );
    }

    if (education.length === 0) {
      pushPlatformIssue(
        "Naukri",
        "Education section missing",
        "Add degree, college, and dates so your profile meets standard recruiter expectations.",
        8,
      );
      pushPlatformIssue(
        "LinkedIn",
        "Education section missing",
        "Add education details to improve profile completeness.",
        6,
      );
    }

    if (hasText(jobDescription) && keywordMatch.matchRatio < 0.35) {
      pushPlatformIssue(
        "Naukri",
        "Low job-keyword alignment",
        "Use more of the target job keywords in your summary, skills, and experience bullets.",
        10,
      );
      pushPlatformIssue(
        "LinkedIn",
        "Low keyword alignment",
        "Mirror key job terms naturally in your headline, about section, and experience.",
        8,
      );
    }

    if (!hasText(analyzedResume?.linkedin)) {
      pushPlatformIssue(
        "LinkedIn",
        "LinkedIn profile link missing",
        "Add your LinkedIn URL to the resume for stronger recruiter trust and profile continuity.",
        4,
      );
    }

    return {
      problems: profileProblems,
      naukri: clampScore(naukriScore),
      linkedin: clampScore(linkedinScore),
    };
  };
  const runATSAnalysis = () => {
    if (!resumeData) return;

    const problems = [];
    let atsScore = 100;
    const nextQualitySnapshot = buildResumeQualitySnapshot(resumeData);
    const analyzedResume = nextQualitySnapshot.correctedResume || resumeData;
    const selectedResumeType = getSelectedResumeType();
    const detectedResumeType =
      selectedResumeType || getResumeExperienceLevel(analyzedResume);
    const isExperiencedResume = detectedResumeType === "experienced";

    const experiences = Array.isArray(analyzedResume?.experience)
      ? analyzedResume.experience.filter(hasStartedExperienceEntry)
      : [];
    const education = analyzedResume?.education || [];
    const totalExperienceValue = getNumericExperienceValue(
      analyzedResume?.totalExperience
    );
    const relevantExperienceValue = getNumericExperienceValue(
      analyzedResume?.relevantExperience
    );
    const summaryText = stripHTML(analyzedResume?.summary || "");
    const objectiveText = stripHTML(analyzedResume?.objective || "");
    const achievementsText = stripHTML(analyzedResume?.achievements || "");

    const skills = Array.isArray(analyzedResume?.skillGroups)
      ? analyzedResume.skillGroups.flatMap((g) => g?.skills || [])
      : analyzedResume?.skillGroups?.skills || [];

    const resumeText = stripHTML(
      JSON.stringify(analyzedResume, null, 0)
    ).replace(/\[object Object\]/g, "");
    const keywordMatch = getJobKeywordMatch(jobDescription, resumeText, skills);

    /* BASIC CHECKS */

    if (!hasText(analyzedResume?.name)) {
      problems.push({
        problem: "Name missing",
        suggestion: "Add your full name",
      });
      atsScore -= 10;
    }

    if (!hasText(analyzedResume?.email)) {
      problems.push({
        problem: "Email missing",
        suggestion: "Add email",
      });
      atsScore -= 10;
    }

    if (!hasText(analyzedResume?.phone)) {
      problems.push({
        problem: "Phone missing",
        suggestion: "Add phone number",
      });
      atsScore -= 8;
    }

    if (!hasText(analyzedResume?.location)) {
      problems.push({
        problem: "Location missing",
        suggestion: "Add your current city or location",
      });
      atsScore -= 6;
    }

    if (isExperiencedResume && !hasText(summaryText)) {
      problems.push({
        problem: "Professional summary missing",
        suggestion: "Add a strong professional summary focused on your experience and impact.",
      });
      atsScore -= 8;
    }

    if (!isExperiencedResume && !hasText(summaryText) && !hasText(objectiveText)) {
      problems.push({
        problem: "Career objective missing",
        suggestion:
          "Add a career objective or short profile summary to explain your target role.",
      });
      atsScore -= 8;
    }

    if (!skills || skills.length === 0) {
      problems.push({
        problem: "Skills section empty",
        suggestion: "Add skills relevant to your profession",
      });
      atsScore -= 10;
    }

    if (isExperiencedResume && skills.length > 0 && skills.length < 5) {
      problems.push({
        problem: "Experienced resume has too few skills",
        suggestion: "Add at least 5 role-relevant skills to improve ATS matching.",
      });
      atsScore -= 4;
    }

    if (!isExperiencedResume && skills.length > 0 && skills.length < 3) {
      problems.push({
        problem: "Fresher resume needs more skills",
        suggestion: "Add at least 3 core skills, tools, or technologies.",
      });
      atsScore -= 4;
    }

    if (isExperiencedResume) {
      if (!(Number.isFinite(totalExperienceValue) && totalExperienceValue > 0)) {
        problems.push({
          problem: "Total experience missing",
          suggestion: "Add your total experience in years for an experienced resume.",
        });
        atsScore -= 6;
      }

      if (
        !(Number.isFinite(relevantExperienceValue) && relevantExperienceValue > 0)
      ) {
        problems.push({
          problem: "Relevant experience missing",
          suggestion: "Add your relevant experience in years for the target role.",
        });
        atsScore -= 6;
      }
    }

    /* EXPERIENCE */

    if (isExperiencedResume && experiences.length === 0) {
      problems.push({
        problem: "Experience section missing",
        suggestion:
          "Add at least one work experience entry with role, company, duration, and achievements.",
      });
      atsScore -= 12;
    }

    experiences.forEach((exp, index) => {
      const start = exp?.startDate || exp?.from;
      const end = exp?.endDate || exp?.to;
      const duration = exp?.years;

      if (!hasText(exp?.company)) {
        problems.push({
          problem: `Experience ${index + 1} company missing`,
          suggestion: "Add the company or organization name.",
        });
        atsScore -= 4;
      }

      if (!hasText(exp?.role)) {
        problems.push({
          problem: `Experience ${index + 1} role missing`,
          suggestion: "Add the role or internship title.",
        });
        atsScore -= 4;
      }

      if (!hasText(start)) {
        problems.push({
          problem: `Experience ${index + 1} start date missing`,
          suggestion: "Add start date",
        });
        atsScore -= 5;
      }

      if (!hasText(end) && !hasText(duration)) {
        problems.push({
          problem: `Experience ${index + 1} end date missing`,
          suggestion: "Add an end date or overall duration",
        });
        atsScore -= 5;
      }

      const desc = stripHTML(exp?.description || "");

      if (desc.trim().length < (isExperiencedResume ? 50 : 30)) {
        problems.push({
          problem: `Experience ${index + 1} description too short`,
          suggestion: isExperiencedResume
            ? "Add achievements, metrics, and results."
            : "Add responsibilities, tools used, or project outcomes.",
        });
        atsScore -= 4;
      }

      const remainingSpellingIssues = getTextSuggestions(desc, {
        limit: 2,
        allowlist: [exp.company, exp.project, exp.role],
      });

      remainingSpellingIssues.forEach((issue) => {
        problems.push({
          problem: `Experience ${index + 1} spelling issue "${issue.word}"`,
          suggestion: `Use "${issue.suggestion}"`,
        });
        atsScore -= 1;
      });
    });

    if (experiences.length > 0) {
      analyzeBullets(experiences, problems);
    }

    nextQualitySnapshot.companyIssues.forEach((issue) => {
      problems.push({
        problem: `Experience ${issue.index + 1} company name includes extra text`,
        suggestion: `Use "${issue.suggestion}" as the company name only.`,
      });
      atsScore -= 3;
    });

    nextQualitySnapshot.projectIssues.forEach((issue) => {
      problems.push({
        problem: `Experience ${issue.index + 1} project name is not highlighted`,
        suggestion: `Highlight "${issue.projectName}" or move it into a clear Project line.`,
      });
      atsScore -= 2;
    });

    /* EDUCATION */

    if (!education.length) {
      problems.push({
        problem: "Education section missing",
        suggestion: "Add degree and university",
      });
      atsScore -= 10;
    } else {
      education.forEach((edu, index) => {
        if (!hasText(edu?.degree)) {
          problems.push({
            problem: `Education ${index + 1} degree missing`,
            suggestion: "Add degree",
          });
          atsScore -= 4;
        }

        if (!hasText(edu?.institution)) {
          problems.push({
            problem: `Education ${index + 1} institution missing`,
            suggestion: "Add university",
          });
          atsScore -= 4;
        }

        if (!hasText(edu?.from) || !hasText(edu?.to)) {
          problems.push({
            problem: `Education ${index + 1} dates missing`,
            suggestion: "Add education start and end year",
          });
          atsScore -= 6;
        }
      });
    }

    if (!isExperiencedResume && experiences.length === 0 && !hasText(achievementsText)) {
      problems.push({
        problem: "Fresher resume needs proof of work",
        suggestion:
          "Add internships, projects, certifications, or achievements to strengthen a fresher resume.",
      });
      atsScore -= 6;
    }

    /* KEYWORD DENSITY */

    const density = computeKeywordDensity(resumeText);
    const platformSnapshot = buildPlatformScores({
      isExperiencedResume,
      analyzedResume,
      experiences,
      education,
      skills,
      summaryText,
      objectiveText,
      achievementsText,
      totalExperienceValue,
      relevantExperienceValue,
      resumeText,
      keywordMatch,
    });

    problems.push(...platformSnapshot.problems);

    setKeywordDensity(density);
    setSkillGap(keywordMatch.missingKeywords.slice(0, 10));
    setResumeType(detectedResumeType);
    setQualitySnapshot(nextQualitySnapshot);

    const genericScore = clampScore(atsScore);
    const overallScore = clampScore(
      Math.round(
        (genericScore + platformSnapshot.naukri + platformSnapshot.linkedin) / 3,
      ),
    );

    setScore(overallScore);
    setPlatformScores({
      generic: genericScore,
      naukri: platformSnapshot.naukri,
      linkedin: platformSnapshot.linkedin,
    });
    setIssues(problems);
  };

  useEffect(() => {
    if (!showATS) return;

    runATSAnalysis();
  }, [resumeData, jobDescription, showATS]);

  if (!showATS) return null;

	return (
  <Drawer
    title="AI ATS Resume Analyzer"
    width={560}
    open={showATS}
    onClose={() => setShowATS(false)}
    styles={{
      header: {
        background: drawerBackground,
        borderBottom: "1px solid rgba(148, 163, 184, 0.12)",
        color: titleText,
      },
      body: {
        background: drawerBackground,
        paddingTop: 20,
      },
      content: {
        background: drawerBackground,
      },
    }}
    closeIcon={<span style={{ color: bodyText, fontSize: 18 }}>x</span>}
  >
    {(qualitySnapshot.appliedCorrections.length > 0 ||
      qualitySnapshot.companyIssues.length > 0) && (
      <Alert
        style={{ marginBottom: 16 }}
        type="info"
        showIcon
        message="ATS scored the corrected version of your resume"
        description={`Included ${qualitySnapshot.appliedCorrections.length} spelling updates and ${qualitySnapshot.companyIssues.length} company-name cleanups before scoring.`}
      />
    )}

    {/* SCORE CARD */}
    <div
      style={{
        background: glassCardBackground,
        borderRadius: 18,
        padding: 24,
        textAlign: "center",
        marginBottom: 24,
        border: glassCardBorder,
        boxShadow: "0 18px 44px rgba(2, 6, 23, 0.28)",
      }}
    >
      <Progress
  type="dashboard"
  percent={score}
  size={200}
  gapDegree={90}
  strokeWidth={12}
  trailColor="rgba(148, 163, 184, 0.14)"
  strokeColor={{
    "0%": "#ff4d4f",
    "50%": "#faad14",
    "100%": "#52c41a",
  }}
  format={() => (
    <div>
      <div style={{ fontSize: 12, color: subtleText }}>TOTAL SCORE</div>
      <div style={{ fontSize: 36, fontWeight: 700, color: titleText }}>
        {score}%
      </div>
    </div>
  )}
/>

      <h2 style={{ marginTop: 16, color: titleText }}>ATS Score</h2>

      <Tag
        style={{ fontSize: 14, padding: "4px 12px" }}
        color={score > 80 ? "green" : score > 60 ? "orange" : "red"}
      >
        {score > 80
          ? "Excellent Resume"
          : score > 60
          ? "Needs Improvement"
          : "Weak Resume"}
      </Tag>
      <div style={{ marginTop: 10 }}>
        <Tag color={resumeType === "experienced" ? "blue" : "purple"}>
          Detected: {resumeType === "experienced" ? "Experienced" : "Fresher"}
        </Tag>
      </div>
      <div
        style={{
          marginTop: 16,
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 10,
        }}
      >
        <div
          style={{
            borderRadius: 14,
            padding: 12,
            background: glassCardBackground,
            border: glassCardBorder,
          }}
        >
          <div style={{ fontSize: 11, color: subtleText }}>Generic ATS</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: titleText }}>
            {platformScores.generic}%
          </div>
        </div>
        <div
          style={{
            borderRadius: 14,
            padding: 12,
            background: glassCardBackground,
            border: glassCardBorder,
          }}
        >
          <div style={{ fontSize: 11, color: subtleText }}>Naukri Ready</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: titleText }}>
            {platformScores.naukri}%
          </div>
        </div>
        <div
          style={{
            borderRadius: 14,
            padding: 12,
            background: glassCardBackground,
            border: glassCardBorder,
          }}
        >
          <div style={{ fontSize: 11, color: subtleText }}>LinkedIn Ready</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: titleText }}>
            {platformScores.linkedin}%
          </div>
        </div>
      </div>
    </div>

    {/* ATS METRICS ROW */}
      <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
        gap: 12,
        marginBottom: 24,
      }}
    >
      <div
        style={{
          border: glassCardBorder,
          borderRadius: 14,
          padding: 16,
          textAlign: "center",
          background: glassCardBackground,
        }}
      >
        <Text style={{ color: subtleText }}>Keywords</Text>
        <h3 style={{ color: titleText }}>{keywordDensity.length}</h3>
      </div>

      <div
        style={{
          border: glassCardBorder,
          borderRadius: 14,
          padding: 16,
          textAlign: "center",
          background: glassCardBackground,
        }}
      >
        <Text style={{ color: subtleText }}>Missing Skills</Text>
        <h3 style={{ color: titleText }}>{skillGap.length}</h3>
      </div>

      <div
        style={{
          border: glassCardBorder,
          borderRadius: 14,
          padding: 16,
          textAlign: "center",
          background: glassCardBackground,
        }}
      >
        <Text style={{ color: subtleText }}>Issues Found</Text>
        <h3 style={{ color: titleText }}>{issues.length}</h3>
      </div>

      <div
        style={{
          border: glassCardBorder,
          borderRadius: 14,
          padding: 16,
          textAlign: "center",
          background: glassCardBackground,
        }}
      >
        <Text style={{ color: subtleText }}>Auto Fixes</Text>
        <h3 style={{ color: titleText }}>
          {qualitySnapshot.appliedCorrections.length}
        </h3>
      </div>
    </div>

    {/* MISSING SKILLS */}
    {skillGap.length > 0 && (
      <div style={{ marginBottom: 24 }}>
        <Divider orientation="left" style={{ color: titleText }}>
          Missing Skills
        </Divider>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {skillGap.map((s, i) => (
            <Tag color="red" key={i}>
              {s}
            </Tag>
          ))}
        </div>
      </div>
    )}

    {/* KEYWORDS */}
    <div style={{ marginBottom: 24 }}>
      <Divider orientation="left" style={{ color: titleText }}>
        Top Resume Keywords
      </Divider>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {keywordDensity.map(([word, count]) => (
          <Tag color="blue" key={word}>
            {word} ({count})
          </Tag>
        ))}
      </div>
    </div>

    {/* SUGGESTIONS */}
    <div>
      <Divider orientation="left" style={{ color: titleText }}>
        ATS Suggestions
      </Divider>

      {issues.length === 0 ? (
        <div
          style={{
            background: "rgba(22, 163, 74, 0.14)",
            border: "1px solid rgba(134, 239, 172, 0.3)",
            padding: 16,
            borderRadius: 14,
          }}
        >
          <Space>
            <CheckCircleOutlined style={{ color: "#22c55e" }} />
            <span style={{ color: bodyText }}>Your resume is ATS optimized</span>
          </Space>
        </div>
      ) : (
        <List
          rowKey={(item, index) => `${item.problem}-${index}`}
          dataSource={issues}
          renderItem={(item) => (
            <List.Item
              style={{
                background: glassCardBackground,
                borderRadius: 14,
                padding: 12,
                marginBottom: 10,
                border: glassCardBorder,
              }}
            >
              <Space direction="vertical">
                <Space>
                  <WarningOutlined style={{ color: "#f59e0b" }} />
                  <Text strong style={{ color: titleText }}>
                    {item.problem}
                  </Text>
                </Space>

                <Text style={{ color: bodyText }}>{item.suggestion}</Text>
              </Space>
            </List.Item>
          )}
        />
      )}
    </div>
  </Drawer>
);
}
